import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Download, Eye, CheckCircle, AlertTriangle, 
  Calendar, Clock, ExternalLink, Shield
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * HostAgreementsPage - View signed host agreements
 * 
 * Shows:
 * - Host Service Agreement status
 * - Signed date and version
 * - View/Download buttons
 * - Prompt to sign if not yet completed
 */

const HostAgreementsPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgreement();
  }, []);

  const fetchAgreement = async () => {
    try {
      const res = await api.get('/host/agreement-status');
      setAgreement(res.data);
    } catch (error) {
      console.error('Error fetching agreement:', error);
      // If no agreement found, show unsigned state
      setAgreement({ signed: false });
    } finally {
      setLoading(false);
    }
  };

  const handleViewAgreement = () => {
    // Navigate to the host agreement page to view the full agreement
    navigate('/host-agreement?view=true');
  };

  const handleDownloadPdf = async () => {
    try {
      toast.info('Generating download link...');
      const res = await api.get('/users/host-agreement/download');
      if (res.data?.download_url) {
        window.open(res.data.download_url, '_blank');
        toast.success('Agreement download started');
      } else {
        toast.error('Download URL not available — agreement PDF may not have been generated yet');
        // Fallback: Open agreement in new tab
        window.open('/host-agreement?view=true', '_blank');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
      // Fallback: Open agreement in new tab
      window.open('/host-agreement?view=true', '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <HostAppShell title="Agreements" activeId="agreements">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Page Header */}
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Your Agreements</h2>
          <p className="text-slate-500 mt-1">View and manage your signed agreements with HireHub</p>
        </div>

        {loading ? (
          <Card className="border-slate-100">
            <CardContent className="py-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
            </CardContent>
          </Card>
        ) : agreement?.signed ? (
          /* Signed Agreement Card */
          <Card className="border-green-200 bg-green-50/30">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Host Service Agreement</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700 font-medium">Signed</span>
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  Active
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Agreement Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-lg p-3 border border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">Version</p>
                  <p className="font-medium text-slate-900">{agreement.version || 'v1.0'}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-100">
                  <p className="text-xs text-slate-500 mb-1">Date Signed</p>
                  <p className="font-medium text-slate-900">{formatDate(agreement.signed_at) || 'N/A'}</p>
                </div>
              </div>

              {/* Agreement Summary */}
              <div className="bg-white rounded-lg p-4 border border-slate-100 mb-4">
                <h4 className="font-medium text-slate-900 mb-2">What this agreement covers:</h4>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Your responsibilities as a vehicle host on HireHub</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Platform fees and commission structure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Insurance and protection coverage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Dispute resolution and claims process</span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2"
                  onClick={handleViewAgreement}
                  data-testid="view-agreement-btn"
                >
                  <Eye className="w-4 h-4" />
                  View Agreement
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2"
                  onClick={handleDownloadPdf}
                  data-testid="download-agreement-btn"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Unsigned Agreement Card */
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Host Service Agreement</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-amber-700 font-medium">Not signed yet</span>
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                  Required
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="bg-white rounded-lg p-4 border border-amber-200 mb-4">
                <p className="text-slate-600">
                  You need to review and sign the Host Service Agreement before you can publish vehicle listings 
                  on HireHub. This agreement outlines your responsibilities and protections as a host.
                </p>
              </div>

              <Button 
                className="w-full bg-[#004B8D] hover:bg-[#003a6d] gap-2"
                onClick={() => navigate('/host-agreement')}
                data-testid="sign-agreement-btn"
              >
                <FileText className="w-4 h-4" />
                Review & Sign Agreement
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Additional Info Card */}
        <Card className="border-slate-100">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#004B8D] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-slate-900">Need help understanding the agreement?</h4>
                <p className="text-sm text-slate-500 mt-1">
                  Our support team is available to answer any questions about your Host Service Agreement. 
                  Contact us at <a href="mailto:support@hirehubaustralia.com" className="text-[#004B8D] hover:underline">support@hirehubaustralia.com</a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </HostAppShell>
  );
};

export default HostAgreementsPage;
